import React, { useState } from 'react';
import HelloModal from '../../../../common/components/hello-modal';
import { useDispatch, useSelector } from 'react-redux';
import Folder from './folder';
import * as deepIntegrationActions from '../../../page/prep-and-send/data/actions/deep-integration';
import { defineMessages, useIntl } from 'react-intl';
import '../modal/index.scss';
import './modal.scss';
import * as selectors from '../../../page/prep-and-send/data/selectors';
import { Dispatch } from 'hellospa/redux/types';

const messages = defineMessages({
  defaultSaveLocation: {
    id: '',
    description: 'Default save location(Folder) for saving a file on remote server',
    defaultMessage: 'Default save location',
  },
  defaultSaveLocationDescription: {
    id: '',
    description: 'Description for file save location',
    defaultMessage: 'All signed copies will be saved in this location going forward.',
  },
  cancel: {
    id: '',
    description: 'Text for cancel button',
    defaultMessage: 'Cancel',
  },
  setAsDefault: {
    id: '',
    description: 'Set the given value as default',
    defaultMessage: 'Set as default',
  },
  clearDefault: {
    id: '',
    description: 'Clear default values',
    defaultMessage: 'Clear defaults',
  },
  saveError: {
    id: '',
    description: 'Error message to be displayed when default value fails to save to the backend',
    defaultMessage: 'Could not save default value',
  },
});

type FolderPickerModalProps = {
  initialFolderSelection?: string, // Current Default?
  onClose: () => void, // call on close
  isModalActive: boolean // is modal active?
  onFolderSelected: (newPath: string) => void,
};

const FolderPickerModal = (props: FolderPickerModalProps) => {

  const { isModalActive, onClose, onFolderSelected } = props;
  const dispatch = useDispatch<Dispatch>();

  const intl = useIntl();

  // Using this because it's only set after a remote save callback
  const savedDefaultSavePath = useSelector(selectors.getExternalDefaultSavePath);

  const [defaultSavePath, setDefaultSavePath] = useState<string | null>(null);
  const [hasValidPath, setHasValidPath] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);

  const currentDefaultSavePath = savedDefaultSavePath || '/';

  // If rootIfNull is true, defaultSavePath will resolve to '/'
  // (Dropbox root) if null or other falsey value
  const onCloseAction = (rootIfNull: boolean = false) => {
    if (defaultSavePath || rootIfNull) {
      onFolderSelected(defaultSavePath || '/');
    }
    onClose();
  };

  const onDefaultFolderChange = (savePath: string | null) => {
    if (typeof savePath === 'string') {
      setHasValidPath(true);
      setDefaultSavePath(savePath);
    } else {
      setHasValidPath(false);
    }
  };

  const saveToBackend = async () => {

    setHasSaveError(false); // Reset if user tries again

    if (defaultSavePath === null) return;

    // Set save path to folder root if default save path is empty string
    const defaultSavePathOrRoot = defaultSavePath === '' ? '/' : defaultSavePath;

    const body = {
      defaultSavePath: defaultSavePathOrRoot,
    };

    const success = await dispatch(deepIntegrationActions.saveDefaultSavePath(body));
    if (success) {
      onCloseAction(true);
    } else {
      setHasSaveError(true);
    }
  };

  const onClearAction = async () => {

    const body = {
      defaultSavePath: '',
    };

    const success = await dispatch(deepIntegrationActions.saveDefaultSavePath(body));
    if (success) {
      onCloseAction();
    } else {
      setHasSaveError(true);
    }
  };

  const resetButtonMarkup = !savedDefaultSavePath ? <>{intl.formatMessage(messages.clearDefault)}</>
    : <a onClick={onClearAction} style={{ display: 'block' }}>
      {intl.formatMessage(messages.clearDefault)}
    </a>;

  return (
    <HelloModal
      isOpen={isModalActive}
      contentLabel={intl.formatMessage(messages.defaultSaveLocation)}
      onRequestClose={onClose}
      footerDisclosure={resetButtonMarkup}
      showCloseIcon={false}
      extraClasses="folderPickerModal"
      buttons={
        [
          {
            type: 'cancel',
            text: intl.formatMessage(messages.cancel),
            onClick: onCloseAction,
          },
          {
            type: 'primary',
            text: intl.formatMessage(messages.setAsDefault),
            disabled: !hasValidPath,
            onClick: saveToBackend,
          },
        ]
      }
    >
      <p className="hello-modal__subHeading">
        {intl.formatMessage(messages.defaultSaveLocationDescription)}
      </p>
      {hasSaveError && <p>intl.formatMessage(messages.saveError)</p>}
      <Folder onFolderSelected={onDefaultFolderChange} initialFolderSelection={currentDefaultSavePath} elementId="dbx-default-chooser" />
    </HelloModal>
  );
};

export default FolderPickerModal;
